import { makeStyles } from '@material-ui/core/styles';
import { Handle, Position } from '@xyflow/react';
import classnames from 'classnames';
import hexToRgba from 'hex-rgba';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { GATriggerType, NodeType } from '../../app/types';
import { changeSeconds } from '../../app/utils';
import IconEnter from '../../assets/images/Icons/IconEnter';
import cartAddImage from '../../assets/images/svg/ic_cart_add.svg';
import cartRemoveImage from '../../assets/images/svg/ic_cart_remove.svg';
import finishOrderImage from '../../assets/images/svg/ic_finish_order.svg';
import GATagWeb from '../../assets/images/svg/ic_tag_web.svg';
import {
  checkNode,
  setDefaultTemplate,
  setIsShowSettingBar,
  setSelectedNode,
} from '../../features/mainSlice';

const useStyles = makeStyles(
  {
    root: {
      width: 240,
      borderRadius: '12px',
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      border: 0,
    },
    selected: {
      border: `solid 1px ${color.$15}`,
    },
    error: {
      border: `solid 1px ${color.$17}!important`,
    },
    highlightPath: {
      border: `solid 1px ${color.$3}!important`,
    },
    handle: {
      border: '0 !important',
      borderRadius: '0 !important',
      background: `${color.$3} !important`,
      width: '1px !important',
      height: '1px !important',
      minWidth: '1px !important',
      minHeight: '1px !important',
    },
    title: {
      borderTopLeftRadius: '12px',
      borderTopRightRadius: '12px',
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$15,
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      background: hexToRgba(color.$15, 6),
      '& > svg': {
        marginRight: 2,
      },
    },
    errorTitle: {
      color: color.$17,
      background: hexToRgba(color.$17, 6),
    },
    content: {
      padding: '12px 16px',
    },
    condition: {
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1.38',
      letterSpacing: 'normal',
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 0',
      background: color.$19,
      '& > img': {
        marginRight: 4,
      },
    },
  },
  {
    name: 'GACondition',
  },
);

const GAConditionComponent = ({ id, data, isConnectable }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isErrorNodeIds = useSelector(store => store.ma.isErrorNodeIds);
  const selectedNode = useSelector(store => store.ma.selectedNode);
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const allPathNodes = useSelector(store => store.ma.highlightPathNodes) || [];
  const isPreview = useSelector(store => store.ma.isPreview);

  const targetNode = useMemo(
    () => nodes.find(n => n?.id === id) || {},
    [nodes],
  );

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === data?.groupId) || {},
    [nodes],
  );

  const { triggerType, timeType, time } = groupNode?.data || {};

  const contentImg = useMemo(() => {
    switch (triggerType) {
      case GATriggerType.GAVIEW:
        return GATagWeb;
      case GATriggerType.GAREMOVEFROMCART:
        return cartRemoveImage;
      case GATriggerType.GAADDTOCART:
        return cartAddImage;
      case GATriggerType.GAPURCHASE:
      default:
        return finishOrderImage;
    }
  }, [triggerType]);

  const isHighlightPath = isPreview && !!allPathNodes.find(n => n?.id === id);
  const isSelected = selectedNode?.id === data?.groupId;
  const isError =
    !!~isErrorNodeIds.indexOf(data?.groupId) &&
    triggerType === GATriggerType.GAVIEW &&
    !checkNode({ type: NodeType.GACONDITION, data });

  const onSelect = () => {
    if (!isSelected && groupNode) {
      dispatch(setSelectedNode(isPreview ? targetNode : groupNode));
      dispatch(
        setDefaultTemplate({
          isShow: false,
        }),
      );
    }

    dispatch(setIsShowSettingBar(true));
  };

  const timeText = useMemo(() => {
    if (triggerType === GATriggerType.GAVIEW) {
      return t(`GAConditionNode.${GATriggerType.GAVIEW}`, {
        view: data?.pageViewTimes || 0,
      });
    }
    return (
      t(`GAConditionNode.${timeType}`, {
        time: changeSeconds(time, timeType) || 30,
      }) + t(`GAConditionNode.${data?.type}.${triggerType}`)
    );
  }, [timeType, time, data?.pageViewTimes]);

  return (
    <div>
      <Handle
        className={classes.handle}
        type="target"
        position={Position.Top}
        style={{ top: 0 }}
        isConnectable={isConnectable}
      />
      <div
        className={classnames(classes.root, {
          [classes.selected]: isSelected,
          [classes.error]: isError,
          [classes.highlightPath]: isHighlightPath,
        })}
        onClick={onSelect}
      >
        <div
          className={classnames(classes.title, {
            [classes.errorTitle]: isError,
          })}
        >
          <IconEnter backgroundColor={isError ? color.$17 : color.$15} />
          {t('add-GAConditions')}
        </div>
        <div className={classes.content}>
          <div className={classes.condition}>
            <img src={contentImg} alt="click" />
            {timeText}
          </div>
        </div>
      </div>
      <Handle
        className={classes.handle}
        type="source"
        position={Position.Bottom}
        style={{ bottom: 0 }}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export default GAConditionComponent;
